// Batch C (AB#2195, G-40) — Super Admin cancels a customer booking, with a
// mandatory reason. Says what will happen (customer and salon are told; it
// can't be undone) before the admin confirms. The reason stays in the audit
// log; it is not shown to the salon or the customer. A refusal from the server
// is explained in the admin's language, mapped from its stable errorCode.

import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAdminCancelBooking } from '../../../hooks/useAdminSupport';
import { formatAppointmentDateTime } from '../../../lib/appointmentTime';
import { normalizeApiError } from '../../../types/api-errors';
import styles from './Support.module.css';
import { useDialogDismiss } from './useDialogDismiss';

export const CANCEL_REASON_MAX = 500;

export interface CancelBookingTarget {
    appointmentId: number;
    appointmentDate: string;
    serviceName: string;
    customerName: string;
    businessName?: string;
}

interface AdminCancelBookingModalProps {
    booking: CancelBookingTarget;
    onClose: () => void;
    /** Called after the booking was cancelled. */
    onCancelled?: () => void;
}

const REFUSAL_KEYS: Record<string, string> = {
    'APPOINTMENT.STATUS_TRANSITION_INVALID': 'superadmin.support.cancel.error.notOpen',
    'ADMIN.CANCEL_REASON_REQUIRED': 'superadmin.support.cancel.error.reasonRequired',
    'ADMIN.CANCEL_REASON_TOO_LONG': 'superadmin.support.cancel.error.reasonTooLong',
};

const AdminCancelBookingModal: React.FC<AdminCancelBookingModalProps> = ({ booking, onClose, onCancelled }) => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const cancel = useAdminCancelBooking();
    const [reason, setReason] = useState('');
    const [errorKey, setErrorKey] = useState<string | null>(null);

    useEffect(() => {
        setReason('');
        setErrorKey(null);
    }, [booking.appointmentId]);

    const trimmed = reason.trim();
    const submitting = cancel.isPending;

    const handleClose = () => {
        if (!submitting) onClose();
    };
    // A stray click outside must not throw away a reason the admin has typed.
    const handleBackdrop = () => {
        if (!trimmed) handleClose();
    };
    const dialogRef = useDialogDismiss<HTMLDivElement>(handleClose);

    const handleConfirm = async () => {
        if (!trimmed) {
            setErrorKey('superadmin.support.cancel.error.reasonRequired');
            return;
        }
        setErrorKey(null);
        try {
            await cancel.mutateAsync({ appointmentId: booking.appointmentId, reason: trimmed });
            showToast(t('superadmin.support.cancel.toast.done'), 'success');
            onCancelled?.();
            onClose();
        } catch (err: unknown) {
            const { code, status } = normalizeApiError(err);
            setErrorKey(
                REFUSAL_KEYS[code] ??
                    (status === 404 ? 'superadmin.support.cancel.error.notFound' : 'superadmin.support.cancel.error.generic'),
            );
        }
    };

    return (
        <div className={styles.modalOverlay} role="presentation" onClick={handleBackdrop}>
            <div
                ref={dialogRef}
                tabIndex={-1}
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-cancel-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 id="admin-cancel-title" className={styles.modalTitle}>
                        {t('superadmin.support.cancel.title')}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label={t('superadmin.bookings.close')}
                    >
                        &times;
                    </button>
                </div>

                <div className={styles.modalContent}>
                    <dl className={styles.summary}>
                        <dt>{t('superadmin.support.col.booking')}</dt>
                        <dd><bdi>#{booking.appointmentId}</bdi></dd>
                        {booking.businessName ? (
                            <>
                                <dt>{t('superadmin.support.col.salon')}</dt>
                                <dd><bdi>{booking.businessName}</bdi></dd>
                            </>
                        ) : null}
                        <dt>{t('superadmin.bookings.col.dateTime')}</dt>
                        <dd>{booking.appointmentDate ? formatAppointmentDateTime(booking.appointmentDate, language) : '—'}</dd>
                        <dt>{t('superadmin.bookings.col.service')}</dt>
                        <dd><bdi>{booking.serviceName || '—'}</bdi></dd>
                        <dt>{t('superadmin.bookings.col.customer')}</dt>
                        <dd><bdi>{booking.customerName || '—'}</bdi></dd>
                    </dl>

                    <p className={styles.body}>{t('superadmin.support.cancel.body')}</p>

                    <div className={styles.field}>
                        <label htmlFor="admin-cancel-reason" className={styles.label}>
                            {t('superadmin.support.reason.label')}
                        </label>
                        <textarea
                            id="admin-cancel-reason"
                            className={styles.textarea}
                            value={reason}
                            maxLength={CANCEL_REASON_MAX}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('superadmin.support.cancel.reason.placeholder')}
                            dir="auto"
                            disabled={submitting}
                            aria-describedby="admin-cancel-reason-hint"
                        />
                        <span id="admin-cancel-reason-hint" className={styles.hint}>
                            {t('superadmin.support.reason.hint')}
                        </span>
                        <span className={styles.counter}>{reason.length}/{CANCEL_REASON_MAX}</span>
                    </div>

                    {errorKey && (
                        <p className={styles.error} role="alert">
                            {t(errorKey)}
                        </p>
                    )}
                </div>

                <div className={styles.modalActions}>
                    <Button
                        label={t('superadmin.support.cancel.keep')}
                        onClick={handleClose}
                        variant="secondary"
                        disabled={submitting}
                    />
                    <Button
                        label={t(submitting ? 'superadmin.support.cancel.cancelling' : 'superadmin.support.cancel.confirm')}
                        onClick={handleConfirm}
                        variant="primary"
                        disabled={submitting || !trimmed}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminCancelBookingModal;
